import { useEffect, useRef, useState } from "react";
import { api } from "./api";

/**
 * Long-running door-session resilience:
 * - HTTP heartbeat to /api/health every 30s
 * - Online/offline detection
 * - Phone screen Wake Lock so the screen stays on during a shift
 * - Auto-recover wake lock on visibility change (re-acquire when tab is foregrounded)
 *
 * Returns { online, lastPingAt, wakeLockSupported, wakeLockActive }.
 */
export default function useKeepAlive({ enabled = true, intervalMs = 30000 } = {}) {
    const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
    const [lastPingAt, setLastPingAt] = useState(null);
    const [wakeLockActive, setWakeLockActive] = useState(false);
    const sentinelRef = useRef(null);

    const wakeLockSupported = typeof window !== "undefined" && "wakeLock" in navigator;

    // Heartbeat
    useEffect(() => {
        if (!enabled) return undefined;
        let cancelled = false;
        const ping = async () => {
            try {
                await api.get("/health");
                if (!cancelled) {
                    setOnline(true);
                    setLastPingAt(new Date());
                }
            } catch {
                if (!cancelled) setOnline(false);
            }
        };
        ping();
        const id = setInterval(ping, intervalMs);
        return () => { cancelled = true; clearInterval(id); };
    }, [enabled, intervalMs]);

    // Online/offline browser events
    useEffect(() => {
        if (!enabled) return undefined;
        const onOnline = () => setOnline(true);
        const onOffline = () => setOnline(false);
        window.addEventListener("online", onOnline);
        window.addEventListener("offline", onOffline);
        return () => {
            window.removeEventListener("online", onOnline);
            window.removeEventListener("offline", onOffline);
        };
    }, [enabled]);

    // Screen wake lock (keeps phone screen ON while page is open)
    useEffect(() => {
        if (!enabled || !wakeLockSupported) return undefined;
        let cancelled = false;

        const acquire = async () => {
            try {
                const s = await navigator.wakeLock.request("screen");
                if (cancelled) {
                    try { await s.release(); } catch { /* */ }
                    return;
                }
                sentinelRef.current = s;
                setWakeLockActive(true);
                s.addEventListener("release", () => setWakeLockActive(false));
            } catch {
                setWakeLockActive(false);
            }
        };

        const onVisibility = () => {
            if (document.visibilityState === "visible") acquire();
        };

        acquire();
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            cancelled = true;
            document.removeEventListener("visibilitychange", onVisibility);
            const s = sentinelRef.current;
            sentinelRef.current = null;
            if (s) { try { s.release(); } catch { /* */ } }
            setWakeLockActive(false);
        };
    }, [enabled, wakeLockSupported]);

    return { online, lastPingAt, wakeLockSupported, wakeLockActive };
}
